import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { faFilter as faFilterSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Form, Formik, FormikValues, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/Modal';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useToggle } from '~/hooks/useToggle';
import { SideLayout } from '~/layouts/side/SideLayout';
import { TextField } from '../form/TextField';

export type FilterProps = {
  onFiltered: (filtered: { [path: string]: string[] }, query?: string) => void;
  onSearch?: (query: string, filters?: any) => void;
};

type FilterFormProps<Values extends FormikValues = FormikValues> = {
  children: React.ReactNode;
  initialValues: Values;
  filterOnLoad?: boolean;
  includeKeyword?: boolean;
  mobileScroll?: boolean;
  cols?: '4' | '5' | 'auto';
} & FilterProps;

export const FilterForm = ({
  children,
  initialValues,
  cols,
  mobileScroll,
  filterOnLoad,
  includeKeyword,
  onFiltered,
  onSearch,
}: FilterFormProps) => {
  const { t } = useTranslation();
  const [open, toggleOpen] = useToggle(true);

  useEffect(() => {
    filterOnLoad && onFiltered(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Formik
      initialValues={{ _query: '', ...initialValues }}
      onSubmit={(values: typeof initialValues, { setSubmitting }) => {
        const { _query, ...filters } = values;
        onFiltered(filters, _query);
        if (onSearch && _query != null) {
          onSearch(_query);
        }
        setSubmitting(false);
      }}
    >
      <Form
        className={[
          'bg-primary-container px-4 pb-4 lg:bg-white lg:pt-4',
          mobileScroll
            ? 'w-full flex-nowrap justify-start gap-2 overflow-x-auto px-4 pb-2.5 lg:px-0 lg:pb-0'
            : 'justify-end lg:grid lg:gap-x-4 lg:bg-transparent',
          'flex items-center',
          cols === '5'
            ? 'lg:grid-cols-5'
            : cols === 'auto'
            ? 'lg:auto-cols-auto lg:grid-flow-col'
            : 'lg:grid-cols-4',
        ].join(' ')}
      >
        <SubmitOnChangeEffect />
        <div className='absolute -right-4 -top-4 z-10 hidden bg-white text-xs'>
          <button type='button' className='p-2' onClick={toggleOpen}>
            {t('Filter')}{' '}
            <FontAwesomeIcon
              className={classNames(
                { 'rotate-90': !open },
                'transition-transform'
              )}
              icon={faAngleDown}
            />
          </button>
        </div>
        {(onSearch || includeKeyword) && (
          <TextField
            className='flex-1'
            name='_query'
            label={t('keywordSearch')}
            type={'search'}
          />
        )}
        {open && (
          <InnerWrapper mobileScroll={mobileScroll}>{children}</InnerWrapper>
        )}
      </Form>
    </Formik>
  );
};

const SubmitOnChangeEffect = () => {
  const { values, submitForm } = useFormikContext();

  useEffect(() => {
    const tick = setTimeout(() => submitForm(), 0);
    return () => {
      clearTimeout(tick);
    };
  }, [values, submitForm]);

  return null;
};

const InnerWrapper = (props: any) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const [show, setShow] = useState(false);
  const { values } = useFormikContext();

  let activeFilterCount = 0;

  // Add type assertion to fix the error
  Object.entries(values as Record<string, any>).forEach(([key, value]) => {
    // Skip the query field
    if (key === '_query') return;

    if (value && (Array.isArray(value) ? value.length > 0 : value !== '')) {
      activeFilterCount++;
    }
  });

  // Determine if we have active filters
  const hasActiveFilters = activeFilterCount > 0;

  if (!isMobile || props.mobileScroll) {
    return <>{props.children}</>;
  }

  if (show) {
    return (
      <Modal
        component={ModalContent}
        {...props}
        onClose={() => setShow(false)}
        open
      />
    );
  }

  const style: React.CSSProperties = {
    marginLeft: '2rem',
    padding: '0.5rem 0',
    background: 'var(--bg)',
    borderRadius: 8,
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#00000096',
  };

  return (
    <button
      className='relative ml-2 flex h-[42px] w-[42px] items-center justify-center rounded-full bg-white bg-opacity-20'
      type='button'
      onClick={() => setShow(true)}
    >
      {(show || hasActiveFilters) && (
        <div className='absolute bottom-1.5 right-1.5 flex h-[17px] w-[17px] items-center justify-center rounded-full border border-primary-container border-opacity-80 bg-white text-primary-container'>
          {show ? (
            <span className='text-xs font-bold'>-</span>
          ) : (
            <span className='text-xs'>{activeFilterCount}</span>
          )}
        </div>
      )}
      <FontAwesomeIcon
        // @ts-ignore
        icon={hasActiveFilters ? faFilterSolid : faFilter}
        className='h-5 w-5 text-white'
      />

      <span className='hidden lg:block'>{t('filters')}</span>
    </button>
  );
};

type ModalContentProps = {
  children: React.ReactNode;
  onClose: () => void;
};

const ModalContent = ({ children, onClose }: ModalContentProps) => {
  return (
    <SideLayout>
      <SideLayout.Head onClose={onClose}>Filters</SideLayout.Head>
      <SideLayout.Body className='p-4 pb-0'>{children}</SideLayout.Body>
      <SubmitButton onSuccess={onClose} />
    </SideLayout>
  );
};

type SubmitButtonProps = {
  onSuccess: () => void;
};

const SubmitButton = ({ onSuccess }: SubmitButtonProps) => {
  const { submitForm } = useFormikContext();

  const handleClick = async () => {
    await submitForm();
    onSuccess();
  };

  const { t } = useTranslation();

  return (
    <div className='p-4 pt-0'>
      <button
        onClick={handleClick}
        type='submit'
        className='rounded-lg border border-future-blue px-5 py-2 text-lg font-bold uppercase text-future-blue'
      >
        {t('apply')}
      </button>
    </div>
  );
};
